import React from "react";
import { Box, Grid, Link, Paper, Typography } from "@mui/material";
import { formatUtcToLocal } from "../../helper/utils";
import formatPrice from "../../helper/currencyFormatter";
import type { StateReportModel } from "../../models/StateReportModel";

const urlRegex = /(https?:\/\/[^\s]+)/g;

/** Extracts URLs from instructions and returns instructions text without URLs plus the first URL for the Filing URL box. */
export function stripUrlsFromInstructions(
  text: string | undefined,
): { instructionsOnly: string; filingUrl: string } {
  if (text == null || text === "") return { instructionsOnly: "", filingUrl: "" };
  const urls = text.match(urlRegex) || [];
  const firstUrl = urls[0];
  const filingUrl =
    firstUrl && firstUrl.toUpperCase() !== "N/A" ? firstUrl.trim() : "";
  const instructionsOnly = text.replace(urlRegex, "").replace(/\n{3,}/g, "\n\n").trim();
  return { instructionsOnly, filingUrl };
}

/** Renders text with URLs as clickable links (preserves newlines). */
export function instructionsWithClickableLinks(text: string | undefined): React.ReactNode {
  if (text == null || text === "") return "N/A";
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("http://") || part.startsWith("https://") ? (
          <Link
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ wordBreak: "break-all", color: "primary.main" }}
          >
            {part}
          </Link>
        ) : (
          part
        ),
      )}
    </>
  );
}

export const noticeSectionTitleSx = {
  mb: 1.5,
  color: "#0d4a63",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
};

export const noticeScreenCardSx = {
  borderRadius: 2,
  border: "1px solid #d9e4ec",
  overflow: "hidden",
  "@media print": {
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
  },
};

export const noticeDetailRowSx = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 2,
  py: 1.25,
  borderBottom: "1px solid #e7eef4",
  "@media print": {
    py: 0.5,
    borderBottom: "none",
  },
};

export type NoticeSummaryItem = {
  label: string;
  value: string;
  emphasize?: boolean;
  danger?: boolean;
};

export type NoticePaymentItem = {
  label: string;
  value: string;
  preserveWhitespace?: boolean;
};

export type NoticeStatusItem = {
  label: string;
  value: string;
};

export function formatNoticeTaxAmount(value: string | number | undefined): string {
  if (!value) return "$0.00";
  const numValue =
    typeof value === "string"
      ? parseFloat(value.replace(/[,\$]/g, "").trim())
      : parseFloat(String(value));
  return isNaN(numValue) ? "$0.00" : formatPrice(numValue.toString());
}

export function formatNoticeDate(dateString: string | undefined): string {
  if (!dateString) return " ";
  return formatUtcToLocal(dateString, "MM/DD/YYYY");
}

export function isNoticeDueDatePast(dueDate: string | undefined): boolean {
  if (!dueDate) return false;
  const due = new Date(dueDate);
  const today = new Date();
  return due < today;
}

/** Merge instructions + filing URL the same way as publish (`handlePublish` in File Now). */
export function mergeInstructionsForNotice(
  instructions: string | undefined,
  filingUrl: string | undefined,
): string {
  if (filingUrl?.trim()) {
    return `${(instructions || "").trim()}\n\n${filingUrl.trim()}`;
  }
  return instructions || "";
}

/**
 * View model for notice cards: aligns draft preview with persisted `StateReportModel` shape
 * so `stripUrlsFromInstructions` / summary rows match the client-facing notice.
 */
export function buildNoticeViewModelFromRowAndPreview(
  selectedRow: StateReportModel,
  previewData: {
    taxAmount?: string;
    paymentType?: string;
    payTo?: string;
    address?: string;
    instructions?: string;
    filingUrl?: string;
    additionalNotes?: string;
  },
): StateReportModel {
  const mergedInstructions = mergeInstructionsForNotice(
    previewData.instructions,
    previewData.filingUrl,
  );
  return {
    ...selectedRow,
    taxAmount: previewData.taxAmount ?? selectedRow.taxAmount,
    paymentType: previewData.paymentType ?? selectedRow.paymentType,
    payTo: previewData.payTo ?? selectedRow.payTo,
    address: previewData.address ?? selectedRow.address,
    instructions: mergedInstructions,
    additionalNotes: previewData.additionalNotes ?? selectedRow.additionalNotes,
  };
}

export function getNoticeSummaryItems(
  stateReport: StateReportModel,
  states: { stateId: number; stateName: string; postalAbbv?: string }[],
): NoticeSummaryItem[] {
  const stateName =
    states.find((state) => state.stateId === stateReport.stateId)?.stateName || "N/A";
  const dueDatePast = isNoticeDueDatePast(stateReport?.dueDate);
  const dueDateText = stateReport.dueDate ? formatNoticeDate(stateReport.dueDate) : "N/A";
  return [
    { label: "State", value: stateName, emphasize: true },
    { label: "Report Name", value: stateReport.name || "N/A" },
    { label: "Period", value: stateReport.period || "N/A" },
    { label: "Due Date", value: dueDateText, emphasize: true, danger: dueDatePast },
    { label: "Licensee Name", value: stateReport.licensee || "N/A" },
    {
      label: "Client Name",
      value: stateReport.client || stateReport.partnerName || "N/A",
    },
  ];
}

export function getNoticePaymentItems(stateReport: StateReportModel): NoticePaymentItem[] {
  return [
    ...(stateReport?.paymentType
      ? [{ label: "Payment Type", value: stateReport.paymentType || "N/A" }]
      : []),
    { label: "Pay To", value: stateReport.payTo || "N/A" },
    {
      label: "Address",
      value: stateReport.address || "N/A",
      preserveWhitespace: true,
    },
  ];
}

export function getNoticeStatusItems(stateReport: StateReportModel): NoticeStatusItem[] {
  return [
    {
      label: "MISC Sent to Client On",
      value: stateReport?.dateFinalReportSentToClient
        ? formatNoticeDate(stateReport.dateFinalReportSentToClient)
        : formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"),
    },
    {
      label: "Client Marked as Done On",
      value: stateReport?.doneByClientAt ? formatNoticeDate(stateReport.doneByClientAt) : "N/A",
    },
    {
      label: "By Client User",
      value:
        (stateReport?.doneByClientUserDisplayName &&
          String(stateReport.doneByClientUserDisplayName).trim()) ||
        "N/A",
    },
  ];
}

export function NoticeReportSummaryCard({
  title = "Report Summary",
  items,
}: {
  title?: string;
  items: NoticeSummaryItem[];
}) {
  return (
    <Grid item xs={12}>
      <Typography variant="overline" sx={noticeSectionTitleSx}>
        {title}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          ...noticeScreenCardSx,
          backgroundColor: "#eaf5fb",
          p: 3,
          "@media print": {
            backgroundColor: "#E3F2FD !important",
            p: "6px 10px",
          },
        }}
      >
        <Grid container className="print-report-summary-fields">
          {items.map((item) => (
            <Grid
              item
              xs={12}
              md={6}
              key={item.label}
              sx={{
                "&:last-child .MuiBox-root": {
                  borderBottom: "none",
                },
                "&:nth-child(5) .MuiBox-root": {
                  borderBottom: "none",
                },
              }}
            >
              <Box
                className="print-report-summary-row"
                sx={{
                  ...noticeDetailRowSx,
                  py: 0.75,
                  display: "grid",
                  gridTemplateColumns: { xs: "120px 1fr", sm: "160px 1fr" },
                  columnGap: 2,
                  alignItems: "start",
                  justifyContent: "stretch",
                }}
              >
                <Typography
                  className={item.label === "Due Date" ? "due-date-label" : undefined}
                  sx={{
                    fontWeight: 700,
                    color: item.danger ? "#d32f2f" : "#0d4a63",
                    minWidth: 0,
                    fontSize: item.emphasize ? "1rem" : "0.95rem",
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  className={item.label === "Due Date" ? "due-date-value" : undefined}
                  sx={{
                    fontWeight: item.emphasize ? 700 : 500,
                    color: item.danger ? "#d32f2f" : "text.primary",
                    textAlign: "left",
                    whiteSpace: "pre-line",
                    minWidth: 0,
                    wordBreak: "break-word",
                    fontSize: item.emphasize ? "1rem" : "0.95rem",
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Grid>
  );
}

export function NoticeFilingDetailsCard({
  taxAmountFormatted,
  paymentItems,
}: {
  taxAmountFormatted: string;
  paymentItems: NoticePaymentItem[];
}) {
  return (
    <Grid item xs={12} md={5}>
      <Typography variant="overline" sx={noticeSectionTitleSx}>
        Filing Details
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          ...noticeScreenCardSx,
          p: 3,
          "@media print": {
            p: "4px 12px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#fff59d",
            border: "1px solid #f4d03f",
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
            "@media print": {
              backgroundColor: "#FFFF00 !important",
              border: "none",
              borderRadius: 0,
              px: 1,
              py: 0.5,
              mb: 1,
            },
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#5f4700" }}>Tax Amount</Typography>
          <Typography sx={{ fontWeight: 700, color: "#5f4700" }}>{taxAmountFormatted}</Typography>
        </Box>

        {paymentItems.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              ...noticeDetailRowSx,
              borderBottom:
                index === paymentItems.length - 1 ? "none" : noticeDetailRowSx.borderBottom,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                color: "#0d4a63",
                minWidth: { xs: 100, sm: 130 },
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                textAlign: "right",
                whiteSpace: item.preserveWhitespace ? "pre-line" : "normal",
                flex: 1,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Paper>
    </Grid>
  );
}

export function NoticeInstructionsAndNotesCard({
  instructionsOnly,
  filingUrl,
  additionalNotes,
}: {
  instructionsOnly: string;
  filingUrl: string;
  additionalNotes: string | undefined;
}) {
  return (
    <Grid item xs={12} md={7}>
      <Typography variant="overline" sx={noticeSectionTitleSx}>
        Instructions And Notes
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          ...noticeScreenCardSx,
          p: 3,
          "@media print": {
            p: "4px 12px",
          },
        }}
      >
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, color: "#0d4a63", mb: 1 }}>Instructions</Typography>
          <Box
            sx={{
              border: "1px solid #d9e4ec",
              borderRadius: 2,
              backgroundColor: "#fbfdff",
              p: 2,
              whiteSpace: "pre-line",
              lineHeight: 1.7,
              minHeight: 88,
              "@media print": {
                border: "none",
                backgroundColor: "transparent",
                borderRadius: 0,
                p: "2px 0",
                minHeight: "auto",
                lineHeight: 1.15,
              },
            }}
          >
            {instructionsOnly || "N/A"}
          </Box>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, color: "#0d4a63", mb: 1 }}>Filing URL</Typography>
          <Box
            sx={{
              border: "1px solid #d9e4ec",
              borderRadius: 2,
              backgroundColor: "#fbfdff",
              p: 2,
              minHeight: 64,
              display: "flex",
              alignItems: "center",
              "@media print": {
                border: "none",
                backgroundColor: "transparent",
                borderRadius: 0,
                p: "2px 0",
                minHeight: "auto",
              },
            }}
          >
            <Typography
              component="div"
              sx={{
                wordBreak: "break-word",
                "& a": { color: "primary.main" },
              }}
            >
              {filingUrl ? instructionsWithClickableLinks(filingUrl) : "N/A"}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 700, color: "#0d4a63", mb: 1 }}>Additional Notes</Typography>
          <Box
            sx={{
              border: "1px solid #d9e4ec",
              borderRadius: 2,
              backgroundColor: "#fbfdff",
              p: 2,
              whiteSpace: "pre-line",
              lineHeight: 1.7,
              minHeight: 88,
              "@media print": {
                border: "none",
                backgroundColor: "transparent",
                borderRadius: 0,
                p: "2px 0",
                minHeight: "auto",
                lineHeight: 1.35,
              },
            }}
          >
            {additionalNotes || "N/A"}
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}

export function NoticeStatusCard({ items }: { items: NoticeStatusItem[] }) {
  return (
    <Grid item xs={12} className="print-status-section">
      <Typography variant="overline" sx={noticeSectionTitleSx}>
        Status
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          ...noticeScreenCardSx,
          p: 3,
          "@media print": {
            p: "4px 12px 0",
          },
        }}
      >
        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid item xs={12} md={4} key={item.label}>
              <Box
                sx={{
                  border: "1px solid #d9e4ec",
                  borderRadius: 2,
                  backgroundColor: "#fbfdff",
                  p: 2,
                  minHeight: 88,
                  "@media print": {
                    border: "none",
                    backgroundColor: "transparent",
                    borderRadius: 0,
                    p: "2px 0",
                    minHeight: "auto",
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "#5b7282",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    mb: 0.75,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{item.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Grid>
  );
}
